import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { Directive, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { marker as _ } from '@colsen1991/ngx-translate-extract-marker';

import { AppRoute } from '../enums';
import { CheckPinResult } from '../models';
import { ConfigSettingsService, CredentialsService } from '../providers';
import { wait } from '../helpers';

@Directive()
export abstract class PinSaverComponentBase {
  private readonly credentialsService = inject(CredentialsService);
  private readonly configSettingService = inject(ConfigSettingsService);
  private readonly router = inject(Router);

  public hasPinSaved = signal(false);
  public isPinValidating = signal(false);
  public pinConfig = this.configSettingService.appSettings?.pinConfiguration;
  public readonly pinLength = 6;

  public pinForm = new FormGroup({
    pin: new FormControl('', [Validators.required, Validators.minLength(this.pinLength)]),
  });

  public pinResult = signal<CheckPinResult>({ success: false, message: '' });
  public isPinInvalid = computed(() => this.pinResult()?.success === false && this.pinResult()?.message !== '');

  public get pin(): AbstractControl<string> {
    return (this.pinForm.get('pin') as AbstractControl<string>) ?? new FormControl('');
  }

  public async checkAndSavePin(): Promise<void> {
    this.pinValidation('on');
    try {
      this.pinResult.set(await this.checkPin());
      if (this.pinResult().success) {
        await this.savePin();
        await wait(0);
        await this.navigateToStart();
      }
      this.pinForm.reset();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : (err as string) || _('general.text.error');
      this.pinResult.set({ success: false, message: errorMessage });
      this.pinForm.reset();
    } finally {
      this.pinValidation('off');
    }
  }

  public checkPin(): Promise<CheckPinResult> {
    if (this.pinForm.invalid) {
      return Promise.resolve({
        success: false,
        message: '',
      });
    }
    return this.credentialsService.checkPin(this.pin.value);
  }

  public async deletePin(): Promise<void> {
    await this.credentialsService.resetPin();
    this.pinForm.get('pin')?.setValue('');
    this.hasPinSaved.set(false);
  }

  public async init(): Promise<void> {
    this.hasPinSaved.set(await this.credentialsService.hasPinSaved());
  }

  protected abstract setPinDisabledState(isDisabled: boolean): void;

  private async navigateToStart(): Promise<void> {
    await this.router.navigate([AppRoute.DashboardPage]);
  }

  private pinValidation(state: 'on' | 'off'): void {
    const isOn = state === 'on';
    this.isPinValidating.set(isOn);
    this.setPinDisabledState(isOn);
  }

  private async savePin(): Promise<void> {
    await this.credentialsService.setPin(this.pin.value.toString().trim());
    this.hasPinSaved.set(true);
  }
}
